import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api    from '../api/axios';
import socket from '../socket';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [loading,       setLoading]       = useState(false);

  // ── Initial load from REST ────────────────────────────────
  const fetchAll = useCallback(async (silent = true) => {
    if (!user) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit: 30 } });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch { /* silent */ }
    finally { if (!silent) setLoading(false); }
  }, [user]);

  // ── Socket lifecycle — connect when logged in ─────────────
  useEffect(() => {
    if (!user) {
      socket.disconnect();
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Load existing notifications on mount
    fetchAll(false);

    // Connect socket (cookie is sent automatically)
    socket.connect();

    // New notification pushed from server
    const onNotification = (notif) => {
      setNotifications(prev => {
        // Avoid duplicates (e.g. if REST and socket race)
        if (prev.some(n => n._id === notif._id)) return prev;
        return [notif, ...prev].slice(0, 30);
      });
      if (!notif.isRead) setUnreadCount(prev => prev + 1);
    };

    socket.on('notification', onNotification);

    return () => {
      socket.off('notification', onNotification);
      socket.disconnect();
    };
  }, [user, fetchAll]);

  // ── Actions (still use REST — no need to change) ──────────
  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const deleteOne = async (id) => {
    await api.delete(`/notifications/${id}`);
    const removed = notifications.find(n => n._id === id);
    setNotifications(prev => prev.filter(n => n._id !== id));
    if (removed && !removed.isRead) setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const clearRead = async () => {
    await api.delete('/notifications');
    setNotifications(prev => prev.filter(n => !n.isRead));
  };

  const refresh = () => fetchAll(false);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, loading,
      markRead, markAllRead, deleteOne, clearRead, refresh
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
